import { useCallback, useEffect, useMemo, useState } from "react";
import { rbPayrollOwnerService } from "../services/rbPayrollOwner.service";
import {
  RBPayrollDuplicateSource,
  RBPayrollFieldErrors,
  RBPayrollPaymentMethod,
  RBPayrollPaymentOption,
  RBPayrollSetupDraft,
  RBPayrollSetupState,
  RBPayrollStatus,
} from "../types/rbPayrollOwner.types";
import { normalizeDigits } from "../utils/rbPayrollOwner.formatters";
import {
  hasRBPayrollValidationErrors,
  validateRBPayrollSetup,
} from "../validators/rbPayrollOwner.validators";
import { RB_PAYROLL_STEPS } from "../constants/rbPayrollOwner.constants";

export type RBPayrollSetupSectionKey = "banking" | "authorization";
export type RBPayrollSetupMode = "setup" | "manage";
export type RBPayrollSaveResult = "saved" | "invalid" | "error";

const cloneValue = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

const getErrorByPath = (errors: any, path: string): string => {
  const keys = path.split(".");
  let cursor = errors;

  for (let index = 0; index < keys.length; index += 1) {
    if (!cursor) {
      return "";
    }

    cursor = cursor[keys[index]];
  }

  return typeof cursor === "string" ? cursor : "";
};

const buildDraftState = (setup: RBPayrollSetupState): RBPayrollSetupDraft => ({
  directDeposit: cloneValue(setup.directDeposit),
});

interface UseRBPayrollOwnerSettingsArgs {
  account: any;
  location: any;
}

export const useRBPayrollOwnerSettings = ({ account, location }: UseRBPayrollOwnerSettingsArgs) => {
  const [setupState, setSetupState] = useState<RBPayrollSetupState | null>(null);
  const [paymentOptions, setPaymentOptions] = useState<RBPayrollPaymentOption[]>([]);
  const [draftState, setDraftState] = useState<RBPayrollSetupDraft | null>(null);
  const [touchedFields, setTouchedFields] = useState<{ [key: string]: boolean }>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [documentUploadError, setDocumentUploadError] = useState("");
  const [duplicateSources, setDuplicateSources] = useState<RBPayrollDuplicateSource[]>([]);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const activeSetupState = setupState;
  const activeDraftState = draftState || (activeSetupState ? buildDraftState(activeSetupState) : null);

  const loadState = useCallback(async () => {
    if (!account || !location) {
      return;
    }

    setIsPageLoading(true);

    try {
      const [nextSetup, nextPaymentOptions, nextDuplicateSources] = await Promise.all([
        rbPayrollOwnerService.getRBPayrollSetup({ account, location }),
        rbPayrollOwnerService.getPaymentOptions({ account, location }),
        rbPayrollOwnerService.getDuplicateSources(account, String(location?.id || "")),
      ]);

      setSetupState(nextSetup);
      setPaymentOptions(nextPaymentOptions);
      setDuplicateSources(nextDuplicateSources);
      setDraftState(buildDraftState(nextSetup));
      setIsEditing(false);
      setSubmitAttempted(false);
      setTouchedFields({});
      setErrorMessage("");
      setDocumentUploadError("");
    } catch (error) {
      const err: any = error;
      setErrorMessage(err?.message || "Unable to load RB Pay setup.");
    } finally {
      setIsPageLoading(false);
    }
  }, [account, location]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const status = activeSetupState?.approval.status || RBPayrollStatus.NOT_STARTED;
  const mode: RBPayrollSetupMode = status === RBPayrollStatus.NOT_STARTED ? "setup" : "manage";

  const errors: RBPayrollFieldErrors = useMemo(
    () => (activeDraftState ? validateRBPayrollSetup(activeDraftState) : {}),
    [activeDraftState]
  );

  const isDirty = useMemo(() => {
    if (!activeSetupState || !activeDraftState) {
      return false;
    }

    return JSON.stringify(buildDraftState(activeSetupState)) !== JSON.stringify(activeDraftState);
  }, [activeDraftState, activeSetupState]);

  // Step completeness — Authorization is its own step. Empty banking now produces
  // "required" errors (empty is no longer treated as cleared) so it never shows
  // complete; an all-zero section is the explicit "remove" state, validates
  // cleanly, and therefore reads as complete.
  const ddErrors = errors.directDeposit || {};
  const bankingComplete = !ddErrors.institutionNumber && !ddErrors.transitNumber && !ddErrors.accountNumber && !ddErrors.document;
  const authComplete = Boolean(activeDraftState?.directDeposit.authorizationAccepted);

  const steps = useMemo(
    () => [
      {
        key: "banking",
        label: RB_PAYROLL_STEPS.banking.label,
        caption: RB_PAYROLL_STEPS.banking.caption,
        complete: bankingComplete,
      },
      {
        key: "authorization",
        label: RB_PAYROLL_STEPS.authorization.label,
        caption: RB_PAYROLL_STEPS.authorization.caption,
        complete: authComplete,
      },
    ],
    [authComplete, bankingComplete]
  );

  const isSetupComplete = steps.every((step) => step.complete);
  const activeStepKey = (steps.find((step) => !step.complete) || steps[steps.length - 1])?.key;

  const getFirstInvalidSection = useCallback((): RBPayrollSetupSectionKey | null => {
    if (ddErrors.institutionNumber || ddErrors.transitNumber || ddErrors.accountNumber || ddErrors.document) {
      return "banking";
    }
    if (ddErrors.authorizationAccepted) {
      return "authorization";
    }
    return null;
  }, [ddErrors.accountNumber, ddErrors.authorizationAccepted, ddErrors.document, ddErrors.institutionNumber, ddErrors.transitNumber]);

  const markFieldTouched = useCallback((path: string) => {
    setTouchedFields((currentValue) => ({ ...currentValue, [path]: true }));
  }, []);

  const updateDraft = useCallback(
    (path: string, value: any) => {
      setDraftState((currentDraft) => {
        const baseDraft = currentDraft || (activeSetupState ? buildDraftState(activeSetupState) : { directDeposit: {} as any });
        const nextDraft = cloneValue(baseDraft);

        if (path === "directDeposit.institutionNumber") {
          nextDraft.directDeposit.institutionNumber = normalizeDigits(value).slice(0, 3);
          nextDraft.directDeposit.authorizationAccepted = false;
        } else if (path === "directDeposit.transitNumber") {
          nextDraft.directDeposit.transitNumber = normalizeDigits(value).slice(0, 5);
          nextDraft.directDeposit.authorizationAccepted = false;
        } else if (path === "directDeposit.accountNumber") {
          nextDraft.directDeposit.accountNumber = normalizeDigits(value).slice(0, 12);
          nextDraft.directDeposit.authorizationAccepted = false;
        } else if (path === "directDeposit.authorizationAccepted") {
          nextDraft.directDeposit.authorizationAccepted = Boolean(value);
        }

        return nextDraft;
      });
    },
    [activeSetupState]
  );

  const getFieldError = useCallback(
    (path: string) => {
      if (!submitAttempted && !touchedFields[path]) {
        return "";
      }
      return getErrorByPath(errors, path);
    },
    [errors, submitAttempted, touchedFields]
  );

  const startEdit = useCallback(() => {
    if (!activeSetupState) {
      return;
    }
    setDraftState(buildDraftState(activeSetupState));
    setIsEditing(true);
    setSubmitAttempted(false);
    setTouchedFields({});
    setErrorMessage("");
    setDocumentUploadError("");
  }, [activeSetupState]);

  const cancelEdit = useCallback(() => {
    if (!activeSetupState) {
      return;
    }
    setDraftState(buildDraftState(activeSetupState));
    setIsEditing(false);
    setSubmitAttempted(false);
    setTouchedFields({});
    setErrorMessage("");
    setDocumentUploadError("");
  }, [activeSetupState]);

  const persist = useCallback(async (): Promise<"saved" | "error"> => {
    if (!activeSetupState || !activeDraftState) {
      return "error";
    }

    setIsSaving(true);
    setErrorMessage("");

    try {
      const savedState = await rbPayrollOwnerService.saveRBPayrollSetup({
        account,
        location,
        setup: { ...activeSetupState, directDeposit: cloneValue(activeDraftState.directDeposit) },
      });
      const savedOptions = await rbPayrollOwnerService.getPaymentOptions({ account, location });

      setSetupState(savedState);
      setPaymentOptions(savedOptions);
      setDraftState(buildDraftState(savedState));
      setIsEditing(false);
      setSubmitAttempted(false);
      setTouchedFields({});
      setDocumentUploadError("");
      window.dispatchEvent(new CustomEvent("rb-payroll-owner-settings-updated"));
      return "saved";
    } catch (error) {
      const err: any = error;
      setErrorMessage(err?.message || "Unable to save RB Pay setup.");
      return "error";
    } finally {
      setIsSaving(false);
    }
  }, [account, activeDraftState, activeSetupState, location]);

  const save = useCallback(async (): Promise<RBPayrollSaveResult> => {
    setSubmitAttempted(true);

    if (!activeDraftState || hasRBPayrollValidationErrors(validateRBPayrollSetup(activeDraftState))) {
      return "invalid";
    }

    return persist();
  }, [activeDraftState, persist]);

  const uploadDocument = useCallback(
    async (file: File) => {
      if (!location) {
        return;
      }

      const document = await rbPayrollOwnerService.uploadBankingDocument(location, file);
      setDraftState((currentDraft) => {
        const baseDraft = currentDraft || (activeSetupState ? buildDraftState(activeSetupState) : { directDeposit: {} as any });
        const nextDraft = cloneValue(baseDraft);
        nextDraft.directDeposit.document = document;
        nextDraft.directDeposit.authorizationAccepted = false;
        return nextDraft;
      });
      markFieldTouched("directDeposit.document");
    },
    [activeSetupState, location, markFieldTouched]
  );

  const removeDocument = useCallback(async () => {
    if (!location || !activeDraftState) {
      return;
    }

    if (activeDraftState.directDeposit.document?.id) {
      await rbPayrollOwnerService.deleteBankingDocument(location, activeDraftState.directDeposit.document.id);
    }

    setDraftState((currentDraft) => {
      const baseDraft = currentDraft || (activeSetupState ? buildDraftState(activeSetupState) : { directDeposit: {} as any });
      const nextDraft = cloneValue(baseDraft);
      nextDraft.directDeposit.document = null;
      nextDraft.directDeposit.authorizationAccepted = false;
      return nextDraft;
    });
    markFieldTouched("directDeposit.document");
  }, [activeDraftState, activeSetupState, location, markFieldTouched]);

  // Copy another location's RB Pay banking setup into this location in one
  // step via the duplicate endpoint. On success the setup state is updated
  // directly (no review/re-authorize step) and the caller shows a success modal.
  const submitDuplicate = useCallback(
    async (fromLocationId: string): Promise<"saved" | "error"> => {
      if (!account || !location) {
        return "error";
      }

      setIsDuplicating(true);
      setErrorMessage("");

      try {
        const duplicatedState = await rbPayrollOwnerService.duplicateRBPayrollSetup({
          account,
          fromLocationId,
          toLocation: location,
        });

        setSetupState(duplicatedState);
        setDraftState(buildDraftState(duplicatedState));
        setIsEditing(false);
        setSubmitAttempted(false);
        setTouchedFields({});
        setDocumentUploadError("");
        window.dispatchEvent(new CustomEvent("rb-payroll-owner-settings-updated"));
        return "saved";
      } catch (error) {
        const err: any = error;
        setErrorMessage(err?.message || "Unable to copy RB Pay setup.");
        return "error";
      } finally {
        setIsDuplicating(false);
      }
    },
    [account, location]
  );

  const selectRBPayroll = useCallback(async () => {
    if (!location) {
      return;
    }

    await rbPayrollOwnerService.savePaymentOption({
      account,
      location,
      paymentMethod: RBPayrollPaymentMethod.RB_PAYROLL,
    });

    const nextOptions = await rbPayrollOwnerService.getPaymentOptions({ account, location });
    setPaymentOptions(nextOptions);
    window.dispatchEvent(new CustomEvent("rb-payroll-owner-settings-updated"));
  }, [account, location]);

  const isSaveDisabled = isSaving || (isEditing && !isDirty);

  return {
    isPageLoading,
    mode,
    status,
    setupState: activeSetupState,
    draftState: activeDraftState,
    paymentOptions,
    duplicateSources,
    isEditing,
    isDirty,
    isSaving,
    isSaveDisabled,
    isDuplicating,
    errorMessage,
    documentUploadError,
    setDocumentUploadError,
    statementPortalUrl: activeSetupState?.statementPortalUrl || "",
    contactEmail: activeSetupState?.contactEmail || "",
    steps,
    activeStepKey,
    isSetupComplete,
    startEdit,
    cancelEdit,
    save,
    updateDraft,
    markFieldTouched,
    getFieldError,
    getFirstInvalidSection,
    uploadDocument,
    removeDocument,
    submitDuplicate,
    selectRBPayroll,
    refresh: loadState,
  };
};
